import { useEffect } from 'react';

export const SITE = 'https://dotlinkapp.com';
const DEFAULT_TITLE = 'DotLink | QR Code Management';
const DEFAULT_IMAGE = `${SITE}/promo/dotlink-post.jpg`;

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  return el;
};
const meta = (attr, name) => upsert(`meta[${attr}="${name}"]`, () => {
  const m = document.createElement('meta');
  m.setAttribute(attr, name);
  return m;
});

// Per-page <title>, description, canonical, Open Graph / Twitter tags, robots and
// FAQ JSON-LD. Plain DOM (no dependency) so the prerender step captures them in
// the saved HTML, which is what Google / WhatsApp / LinkedIn read.
export const useSeo = ({ title, description, path, image, noindex, jsonLd } = {}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      meta('name', 'description').setAttribute('content', description);
      meta('property', 'og:description').setAttribute('content', description);
      meta('name', 'twitter:description').setAttribute('content', description);
    }
    if (title) {
      meta('property', 'og:title').setAttribute('content', title);
      meta('name', 'twitter:title').setAttribute('content', title);
    }
    meta('property', 'og:type').setAttribute('content', 'website');
    meta('name', 'twitter:card').setAttribute('content', 'summary_large_image');
    const img = image || DEFAULT_IMAGE;
    meta('property', 'og:image').setAttribute('content', img);
    meta('name', 'twitter:image').setAttribute('content', img);

    if (path) {
      const url = SITE + path;
      upsert('link[rel="canonical"]', () => { const l = document.createElement('link'); l.rel = 'canonical'; return l; }).setAttribute('href', url);
      meta('property', 'og:url').setAttribute('content', url);
    }

    let robots = null;
    if (noindex) {
      robots = meta('name', 'robots');
      robots.setAttribute('content', 'noindex, nofollow');
    }

    const lds = (jsonLd ? [].concat(jsonLd) : []).map((data) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo', '1');
      el.text = JSON.stringify(data);
      document.head.appendChild(el);
      return el;
    });

    return () => {                       // leaving the page: don't leak its tags into the next one
      document.title = prevTitle || DEFAULT_TITLE;
      if (robots) robots.remove();
      lds.forEach((el) => el.remove());
      document.head.querySelector('link[rel="canonical"]')?.remove();
    };
  }, [title, description, path, image, noindex, jsonLd]);
};

export const faqJsonLd = (faq) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faq.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
});
